import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";

export default function Privacy() {
  return (
    <div className="page page-privacy" style={{ maxWidth: "800px", margin: "0 auto", padding: "2rem 1rem" }}>
      <Helmet>
        <title>Privacy Policy | Nova</title>
        <meta name="description" content="Learn how Nova respects your privacy, safeguards your workspace data, and uses device fingerprinting to prevent rate-limit bypasses." />
      </Helmet>

      <h1 className="page-title" style={{ fontSize: "2.2rem", marginBottom: "1rem" }}>Privacy Policy</h1>
      <p className="dashboard-lead" style={{ fontSize: "1.1rem", color: "var(--text-muted)", marginBottom: "2rem" }}>
        Last updated: June 30, 2026. Learn how we handle your data, ensure multi-tenant security, and use device fingerprinting.
      </p>

      <div className="card" style={{ padding: "2.5rem", display: "flex", flexDirection: "column", gap: "1.8rem", lineHeight: "1.6" }}>

        <section>
          <h2 style={{ fontSize: "1.4rem", color: "var(--text-heading)", marginBottom: "0.5rem" }}>1. Introduction</h2>
          <p>
            At Nova, we are committed to protecting your privacy and workspace integrity. This Privacy Policy details our data collection practices, the security measures we employ, and how we leverage device fingerprinting to secure our resources fairly for all users.
          </p>
        </section>

        <section>
          <h2 style={{ fontSize: "1.4rem", color: "var(--text-heading)", marginBottom: "0.5rem" }}>2. Device Fingerprinting & Anonymous Audits</h2>
          <p>
            To prevent plan bypasses, spamming, and automated abuse of our free anonymous audit tools, we implement non-invasive <strong>device fingerprinting</strong>. When you perform an anonymous SEO audit, our system generates a unique, anonymous hash based on standard browser attributes.
          </p>
          <div style={{ background: "#f8fafc", border: "1px solid var(--border)", borderRadius: "8px", padding: "1rem", marginTop: "0.8rem" }}>
            <h3 style={{ fontSize: "1rem", margin: "0 0 0.5rem 0", color: "var(--text-heading)" }}>Attributes We Analyze:</h3>
            <ul style={{ paddingLeft: "1.2rem", margin: 0 }}>
              <li>User-Agent string (Browser version and OS)</li>
              <li>Configured system languages and locale settings</li>
              <li>Device screen resolution and color depth</li>
              <li>Timezone and platform settings</li>
              <li>A local, hardware-based canvas rendering check</li>
            </ul>
          </div>
          <p style={{ marginTop: "0.8rem" }}>
            <strong>Privacy Guarantee:</strong> This fingerprint is immediately hashed using a cryptographic algorithm (SHA-256) into a random string of characters. We do not store the raw attributes, nor do we track your browsing history across other websites. This hash is solely used to enforce the 24-hour anonymous audit limit per device.
          </p>
        </section>

        <section>
          <h2 style={{ fontSize: "1.4rem", color: "var(--text-heading)", marginBottom: "0.5rem" }}>3. Multi-Tenant Workspace Isolation</h2>
          <p>
            For registered users, Nova SEO enforces strict tenant isolation. All audits, monitored sites, project histories, and crawl schedules are strictly scoped to your active <strong>workspace</strong>.
          </p>
          <ul style={{ paddingLeft: "1.2rem" }}>
            <li>No data can spill or leak between different workspaces.</li>
            <li>Members must be explicitly invited to a workspace to access its reports or history.</li>
            <li>Plan limits (including manual audits and active scheduled monitors) are evaluated at the workspace level, not the individual user level, ensuring clean quota tracking.</li>
          </ul>
        </section>

        <section>
          <h2 style={{ fontSize: "1.4rem", color: "var(--text-heading)", marginBottom: "0.5rem" }}>4. How We Use Your Information</h2>
          <p>
            Your account credentials, workspace preferences, and brand assets (such as logos and theme colors for PDF exports) are stored securely on our database. We use this data only to provide the SEO auditing service, generate reports, send automated email alerts, and manage billing.
          </p>
        </section>

        <section>
          <h2 style={{ fontSize: "1.4rem", color: "var(--text-heading)", marginBottom: "0.5rem" }}>5. Your Rights & Choice</h2>
          <p>
            You have full control over your personal and workspace data. At any time, you can edit your profile details, manage active notification emails, update branding templates, or permanently delete your account through your <Link to="/settings" style={{ color: "var(--accent)", textDecoration: "underline" }}>settings</Link>. Account deletion is permanent and wipes all associated workspace data.
          </p>
        </section>

        <section style={{ borderTop: "1px solid var(--border)", paddingTop: "1.5rem", marginTop: "1rem" }}>
          <p style={{ fontSize: "0.9rem", color: "var(--text-muted)", margin: 0 }}>
            If you have any questions or concerns regarding our privacy practices or the technologies we use, please contact us at <a href="mailto:privacy@novaseo.com" style={{ color: "var(--accent)" }}>privacy@novaseo.com</a>.
          </p>
        </section>
      </div>
    </div>
  );
}
